import React, {useState, useEffect, useCallback} from "react";
import {Link} from "react-router-dom";
import {useAuth} from "../contexts/AuthContext";
import {
  getOrganizationUsers,
  exportUserContent,
  getExportJobStatus,
  User,
} from "../services/api";
import Navigation from "./Navigation";
import "../App.css";

const Dashboard: React.FC = () => {
  const {user} = useAuth();
  const [apiKey, setApiKey] = useState("");
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState("");
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // Helper function to extract error message from various error formats
  const extractErrorMessage = useCallback((error: any): string => {
    console.log("extractErrorMessage called with:", error, typeof error);
    
    if (!error) return "An unknown error occurred";
    
    // If it's already a string
    if (typeof error === "string") {
      console.log("Error is string:", error);
      return error;
    }
    
    // If it has a message property (Error object)
    if (error.message && typeof error.message === "string") {
      console.log("Error has message property:", error.message);
      return error.message;
    }
    
    // If it's an error object with error property
    if (error.error) {
      if (typeof error.error === "string") {
        console.log("Error.error is string:", error.error);
        return error.error;
      }
      if (error.error.message && typeof error.error.message === "string") {
        console.log("Error.error.message:", error.error.message);
        return error.error.message;
      }
      // If error.error is an object, try to extract info
      if (typeof error.error === "object") {
        const errorObj = error.error;
        if (errorObj.code && errorObj.message) {
          return `Error ${errorObj.code}: ${errorObj.message}`;
        }
        if (errorObj.type && errorObj.message) {
          return `${errorObj.type}: ${errorObj.message}`;
        }
        const errorStr = JSON.stringify(errorObj);
        if (errorStr.length > 200 || errorStr === "{}") {
          return "Failed to load users. Please check your API key and try again.";
        }
        return errorStr;
      }
    }
    
    // If it's a response object (axios error)
    if (error.response) {
      if (error.response.data) {
        if (typeof error.response.data === "string") return error.response.data;
        if (error.response.data.error) {
          const err = error.response.data.error;
          if (typeof err === "string") return err;
          if (err.message) return err.message;
          return JSON.stringify(err);
        }
        if (error.response.data.message) return error.response.data.message;
      }
      if (error.response.status) {
        return `API Error (${error.response.status}): ${error.response.statusText || "Request failed"}`;
      }
    }
    
    // If it's an object, try to stringify useful parts
    if (typeof error === "object") {
      // Check for common error object patterns
      if (error.code && error.message) {
        return `Error ${error.code}: ${error.message}`;
      }
      if (error.type && error.message) {
        return `${error.type}: ${error.message}`;
      }
      
      const errorStr = JSON.stringify(error);
      console.log("Stringified error:", errorStr);
      // If it's too long or not useful, return generic message
      if (errorStr.length > 200 || errorStr === "{}") {
        return "Failed to load users. Please check your API key and try again.";
      }
      return errorStr;
    }
    
    // Last resort: convert to string
    try {
      return String(error);
    } catch {
      return "Failed to load users. Please check your API key and try again.";
    }
  }, []);

  // Wrapper to ensure error is always a string
  const setErrorSafe = useCallback((errorValue: any) => {
    if (typeof errorValue === "string") {
      setError(errorValue);
    } else {
      setError(extractErrorMessage(errorValue));
    }
  }, [extractErrorMessage]);

  // Ensure error state is always a string (safety check)
  useEffect(() => {
    if (error && typeof error !== "string") {
      console.warn("Error state is not a string, converting:", error);
      const errorMsg = extractErrorMessage(error);
      setError(errorMsg);
    }
  }, [error, extractErrorMessage]);

  useEffect(() => {
    // Poll job status if there's an active export
    if (currentJobId && exporting) {
      const interval = setInterval(async () => {
        try {
          const response = await getExportJobStatus(currentJobId);
          if (response.job) {
            if (
              response.job.status === "completed" ||
              response.job.status === "failed"
            ) {
              setExporting(false);
              setCurrentJobId(null);
              if (response.job.status === "completed") {
                setSuccess(
                    `Export completed! Processed ${response.job.processedItems} items.`
                );
              } else {
                setErrorSafe(response.job.error || "Export failed");
              }
            }
          }
        } catch (err: any) {
          console.error("Error checking job status:", err);
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [currentJobId, exporting]);

  const loadUsers = useCallback(async (key: string) => {
    setLoading(true);
    setError("");
    try {
      const orgUsers = await getOrganizationUsers(key);
      setUsers(orgUsers);
    } catch (err: any) {
      setErrorSafe(err);
      setUsers([]);
      console.error("Error loading users:", err);
    } finally {
      setLoading(false);
    }
  }, [setErrorSafe]);

  useEffect(() => {
    // Load API key from localStorage
    const savedApiKey = localStorage.getItem("chatgpt_api_key");
    if (savedApiKey) {
      setApiKey(savedApiKey);
      loadUsers(savedApiKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApiKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (apiKeyInput.trim()) {
      localStorage.setItem("chatgpt_api_key", apiKeyInput);
      setApiKey(apiKeyInput);
      loadUsers(apiKeyInput);
      setApiKeyInput("");
    } else {
      setErrorSafe("API key is required");
    }
  };

  const handleUserToggle = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map((u) => u.id)));
    }
  };

  const handleExport = async () => {
    if (selectedUsers.size === 0) {
      setErrorSafe("Please select at least one user");
      return;
    }

    if (!apiKey) {
      setErrorSafe("Please provide an API key");
      return;
    }

    setExporting(true);
    setError("");
    setSuccess("");

    try {
      const result = await exportUserContent(
          apiKey,
          Array.from(selectedUsers),
          user?.uid || "anonymous"
      );
      setCurrentJobId(result.jobId);
      setSuccess("Export started! Check the Jobs page for progress.");
    } catch (err: any) {
      setErrorSafe(err);
      setExporting(false);
      console.error("Error starting export:", err);
    }
  };

  return (
    <div>
      <Navigation />
      <div className="container">
        <div className="card">
          <div className="card-header">
            <h1 className="card-title">Export Portal</h1>
          </div>

          {error && <div className="alert alert-error">
            {(() => {
              // Triple-check: ensure we always render a string
              if (typeof error === "string") {
                return error;
              }
              if (error && typeof error === "object") {
                // Try to extract a meaningful message
                const msg = extractErrorMessage(error);
                console.warn("Rendering non-string error, extracted:", msg);
                return msg;
              }
              return String(error || "An unknown error occurred");
            })()}
          </div>}
          {success && <div className="alert alert-success">{success}</div>}

          {!apiKey ? (
            <form onSubmit={handleApiKeySubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="api-key">
                  ChatGPT Compliance API Key
                </label>
                <input
                  id="api-key"
                  type="password"
                  className="form-input"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="Enter your API key"
                  required
                />
                <small style={{color: "#666", marginTop: "0.5rem", display: "block"}}>
                  Your API key will be stored locally in your browser
                </small>
              </div>
              <button type="submit" className="btn btn-primary">
                Set API Key
              </button>
            </form>
          ) : (
            <>
              <div className="flex-between mb-2">
                <div>
                  <p>
                    <strong>API Key:</strong> {apiKey.substring(0, 10)}...
                  </p>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    localStorage.removeItem("chatgpt_api_key");
                    setApiKey("");
                    setUsers([]);
                    setSelectedUsers(new Set());
                  }}
                >
                  Change API Key
                </button>
              </div>

              <div className="form-group">
                <div className="flex-between mb-2">
                  <label className="form-label">
                    Select Users to Export ({selectedUsers.size} selected)
                  </label>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleSelectAll}
                  >
                    {selectedUsers.size === users.length
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                </div>

                {loading ? (
                  <div className="text-center">
                    <div className="spinner"></div>
                    <p>Loading users...</p>
                  </div>
                ) : users.length === 0 ? (
                  <div className="empty-state">
                    <p>No users found. Click "Load Users" to fetch users.</p>
                    <button
                      className="btn btn-primary mt-2"
                      onClick={() => loadUsers(apiKey)}
                    >
                      Load Users
                    </button>
                  </div>
                ) : (
                  <div className="user-list">
                    {users.map((user) => (
                      <div
                        key={user.id}
                        className={`user-item ${
                          selectedUsers.has(user.id) ? "selected" : ""
                        }`}
                        onClick={() => handleUserToggle(user.id)}
                      >
                        <input
                          type="checkbox"
                          className="user-checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                        />
                        <div className="user-info-item">
                          <div className="user-name">
                            {user.name || user.email || user.id}
                          </div>
                          {user.email && (
                            <div className="user-email">{user.email}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleExport}
                  disabled={selectedUsers.size === 0 || exporting}
                >
                  {exporting ? "Exporting..." : "Start Export"}
                </button>
                <Link to="/jobs" className="btn btn-secondary">
                  View Export Jobs
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

